import React from 'react';
import { TrendingUp, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { SectionHeader } from './SectionHeader';

// Growth Analysis Component
export function GrowthAnalysis({ 
  growthChartData, 
  growthMetrics, 
  dateRangeLabel,
  onSeedGrowthData 
}) {
  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      <SectionHeader 
        icon={TrendingUp} 
        title="Growth Analysis" 
        subtitle={`Subscriber movement • ${dateRangeLabel}`}
        action={
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#10B981]" />
              <span className="text-[#9CA3AF]">Joined</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#E63946]" />
              <span className="text-[#9CA3AF]">Left</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#3B82F6]" />
              <span className="text-[#9CA3AF]">Total</span>
            </div>
          </div>
        }
      />
      
      <div className="p-5">
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-[#10B981]/10 rounded-lg p-3 text-center border border-[#10B981]/20">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">TOTAL JOINED</p>
            <p className="text-xl font-bold text-[#10B981]">
              +{growthChartData.reduce((sum, d) => sum + (d.subsIn || 0), 0).toLocaleString()}
            </p>
            {growthChartData.some(d => d.isEstimated) && <p className="text-[9px] text-[#6B7280] italic">partly estimated</p>}
          </div>
          <div className="bg-[#E63946]/10 rounded-lg p-3 text-center border border-[#E63946]/20">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">TOTAL LEFT</p>
            <p className="text-xl font-bold text-[#E63946]">
              -{growthChartData.reduce((sum, d) => sum + (d.subsOut || 0), 0).toLocaleString()}
            </p>
            {growthChartData.some(d => d.isEstimated) && <p className="text-[9px] text-[#6B7280] italic">partly estimated</p>}
          </div>
          <div className="bg-[#3B82F6]/10 rounded-lg p-3 text-center border border-[#3B82F6]/20">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">NET GROWTH</p>
            {(() => {
              const netGrowth = growthChartData.length >= 2 
                ? growthChartData[growthChartData.length - 1]?.subscribers - growthChartData[0]?.subscribers 
                : growthMetrics.net_change || 0;
              return (
                <p className={`text-xl font-bold ${netGrowth > 0 ? 'text-[#3B82F6]' : netGrowth < 0 ? 'text-[#E63946]' : 'text-[#4A4A4D]'}`}>
                  {netGrowth > 0 ? `+${netGrowth.toLocaleString()}` : netGrowth.toLocaleString()}
                </p>
              );
            })()}
          </div>
          <div className="bg-[#8B5CF6]/10 rounded-lg p-3 text-center border border-[#8B5CF6]/20">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">GROWTH RATE</p>
            {(() => {
              const first = growthChartData[0]?.subscribers || 1;
              const last = growthChartData[growthChartData.length - 1]?.subscribers || first;
              const rate = first > 0 ? (((last - first) / first) * 100).toFixed(2) : 0;
              return (
                <p className={`text-xl font-bold ${rate > 0 ? 'text-[#8B5CF6]' : rate < 0 ? 'text-[#E63946]' : 'text-[#4A4A4D]'}`}>
                  {rate > 0 ? `+${rate}%` : `${rate}%`}
                </p>
              );
            })()}
          </div>
        </div>
        
        {growthChartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={growthChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '10px' }} />
              <YAxis yAxisId="left" stroke="#6B7280" style={{ fontSize: '10px' }} />
              <YAxis yAxisId="right" orientation="right" stroke="#3B82F6" style={{ fontSize: '10px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: '#fff' }}
                formatter={(value, name) => {
                  if (name === 'Joined') return [`+${value}`, name];
                  if (name === 'Left') return [`-${value}`, name];
                  return [value?.toLocaleString(), name];
                }}
              />
              <Legend />
              {/* Stacked Bar Chart - Joined at bottom, Left on top */}
              <Bar yAxisId="left" dataKey="subsIn" stackId="growth" fill="#10B981" name="Joined" radius={[0, 0, 0, 0]} />
              <Bar yAxisId="left" dataKey="subsOut" stackId="growth" fill="#E63946" name="Left" radius={[2, 2, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="subscribers" stroke="#3B82F6" strokeWidth={2} dot={false} name="Total Subs" />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[220px] flex items-center justify-center text-center">
            <div>
              <Button onClick={onSeedGrowthData} variant="outline" size="sm" className="mb-2">
                <Activity size={14} className="mr-2" />
                Generate Growth Data
              </Button>
              <p className="text-xs text-[#6B7280]">Click to seed historical data</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GrowthAnalysis;
